import { RazorNumberFormat } from "../../../components/razor-reuse";
import { PlaidTransactionItem } from "../../../redux/type";

// Plaid payment statuses the endpoint accepts for the `status` filter.
export const PLAID_PAYMENT_STATUSES = [
  "PAYMENT_STATUS_INPUT_NEEDED",
  "PAYMENT_STATUS_AUTHORISING",
  "PAYMENT_STATUS_INITIATED",
  "PAYMENT_STATUS_EXECUTED",
  "PAYMENT_STATUS_SETTLED",
  "PAYMENT_STATUS_INSUFFICIENT_FUNDS",
  "PAYMENT_STATUS_FAILED",
  "PAYMENT_STATUS_BLOCKED",
  "PAYMENT_STATUS_REJECTED",
  "PAYMENT_STATUS_CANCELLED",
] as const;

// "PAYMENT_STATUS_INPUT_NEEDED" -> "INPUT NEEDED"
export function formatPaymentStatus(status?: string | null) {
  if (!status) return "---";
  return status.replace(/^PAYMENT_STATUS_/, "").replace(/_/g, " ");
}

// "not_credited" -> "not credited"
export function formatLabel(value?: string | null) {
  return value ? value.replace(/_/g, " ") : "---";
}

export const PAYMENT_STATUS_OPTIONS = [
  ...PLAID_PAYMENT_STATUSES.map((status) => ({
    label: formatPaymentStatus(status),
    value: status,
  })),
  { label: "All Status", value: "" },
];

export const CREDIT_STATUS_OPTIONS = [
  { label: "Credited", value: "credited" },
  { label: "Not Credited", value: "not_credited" },
  { label: "All Credit Status", value: "" },
];

export const CURRENCY_OPTIONS = [
  { label: "GBP", value: "GBP" },
  { label: "EUR", value: "EUR" },
  { label: "All Currencies", value: "" },
];

const BANK_PAYMENT_SUCCESS = new Set([
  "PAYMENT_STATUS_EXECUTED",
  "PAYMENT_STATUS_SETTLED",
]);
const BANK_PAYMENT_FAILED = new Set([
  "PAYMENT_STATUS_INSUFFICIENT_FUNDS",
  "PAYMENT_STATUS_FAILED",
  "PAYMENT_STATUS_BLOCKED",
  "PAYMENT_STATUS_REJECTED",
  "PAYMENT_STATUS_CANCELLED",
]);

export function getBankPaymentBadge(status?: string | null) {
  if (!status) return "";
  if (BANK_PAYMENT_SUCCESS.has(status)) return "success";
  if (BANK_PAYMENT_FAILED.has(status)) return "failed";
  return "pending";
}

// not_credited means the bank sent the money but the wallet wasn't credited,
// so it's flagged red. Values we haven't been given fall back by keyword.
export function getWalletCreditBadge(status?: string | null) {
  if (!status) return "";
  const normalized = status.toLowerCase();
  if (normalized === "not_applicable") return "info";
  if (normalized === "not_credited" || normalized.includes("fail"))
    return "failed";
  if (normalized === "credited") return "success";
  return "pending";
}

// The overall status isn't a documented enum (seen: processing, failed,
// expired), so bucket by keyword rather than an exact-match list.
export function getOverallStatusBadge(status?: string | null) {
  if (!status) return "";
  const normalized = status.toLowerCase();
  if (
    normalized.includes("fail") ||
    normalized.includes("reject") ||
    normalized.includes("cancel") ||
    normalized.includes("block")
  )
    return "failed";
  if (normalized.includes("expire")) return "info";
  if (
    normalized.includes("complete") ||
    normalized.includes("credited") ||
    normalized.includes("success") ||
    normalized.includes("settled")
  )
    return "success";
  return "pending";
}

export function getCustomerName(item?: PlaidTransactionItem | null) {
  const customer = item?.customer;
  if (!customer) return "---";
  const name = `${customer.first_name || ""} ${customer.last_name || ""}`.trim();
  return name || customer.email || "---";
}

// Amounts can arrive as numbers or numeric strings; anything else is shown
// as "---" rather than NaN.
export function toAmount(value?: number | string | null): number | null {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

// RazorNumberFormat renders null as "0.00" and defaults to the NGN symbol, so
// guard both: missing amounts show "---" and a missing currency shows no symbol.
export function formatMoney(
  value?: number | string | null,
  currency?: string | null,
) {
  const amount = toAmount(value);
  if (amount === null) return "---";
  return currency
    ? RazorNumberFormat(amount, { symbol: currency })
    : RazorNumberFormat(amount, { hideSymbol: true });
}
